import { Container, Sprite, Text } from 'pixi.js';
import { AssetLoader } from '../asset/AssetLoader';
import { Easings, TweenGroup } from '../core/Tween';

const FONT_FAMILY = 'system-ui, -apple-system, sans-serif';
/** Default alias the host themes register the coin icon under. Themes
 * MAY override; BalanceDisplay renders the digits without a glyph
 * prefix when the alias hasn't been registered or the texture failed
 * to load. */
const DEFAULT_COIN_ICON_ALIAS = 'shared:icon:coin';
/** Coin sprite display height — matches the amount text's font size so
 * the icon visually balances against the digits beside it. */
const COIN_SPRITE_HEIGHT = 32;

/**
 * Two-row counter showing a "BALANCE" caption above a coin-prefixed numeric
 * value. Used at the top of slot / wheel / bingo scenes.
 *
 * <p>{@link bump} runs a brief scale pulse — designed to fire as each coin
 * lands during the take-win flying-coin animation, so the counter feels like
 * it's "catching" the coins rather than just ticking up.
 *
 * <p><b>Coin glyph.</b> When the active theme registers a {@code coinIcon}
 * via {@link Theme#assets#scene}, the prefix renders as a Pixi
 * {@link Sprite} of the commissioned art. Themes that don't ship icon
 * art (or before assets have loaded) render the digits unprefixed —
 * cleaner than the system coin emoji, which fonts often substitute
 * with a pale moon-sphere fallback glyph.
 */
export interface BalanceDisplayOptions {
  /** Colour of the amount text. Defaults to warm gold. */
  amountColor?: number;
  /** Override for the AssetLoader alias the coin sprite is fetched
   * under. Defaults to {@link DEFAULT_COIN_ICON_ALIAS}. Themes that
   * register the icon under a different alias (or hosts that
   * pre-register a global UI bundle) can swap this here. */
  coinIconAlias?: string;
}

export class BalanceDisplay extends Container {
  private readonly caption: Text;
  private readonly amount: Text;
  private readonly coinSprite: Sprite | null;
  private readonly tweens = new TweenGroup();

  constructor(initialAmount: number, options: BalanceDisplayOptions = {}) {
    super();
    this.caption = new Text({
      text: 'BALANCE',
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: 11,
        fontWeight: '700',
        fill: 0xffffff,
        letterSpacing: 2,
      },
    });
    this.caption.anchor.set(0.5, 0);
    this.caption.alpha = 0.55;
    this.addChild(this.caption);

    // Resolve the coin texture synchronously — by the time the host
    // constructs BalanceDisplay (inside scene.buildContent), the
    // AssetLoader.loadTheme await in scene.preload has already
    // resolved, so the texture is in cache when the theme registered
    // it. When unset OR lookup fails we render the digits with no
    // prefix (the system coin emoji rendered as a moon-sphere on
    // Apple Color Emoji fallback, which we never want to ship).
    const coinAlias = options.coinIconAlias ?? DEFAULT_COIN_ICON_ALIAS;
    const coinTexture = AssetLoader.shared.getTexture(coinAlias);

    this.amount = new Text({
      text: initialAmount.toLocaleString(),
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: 32,
        fontWeight: '800',
        fill: options.amountColor ?? 0xfff59d,
      },
    });
    this.amount.anchor.set(0.5, 0);
    // Amount sits 16px below the caption baseline.
    this.amount.y = 16;
    this.addChild(this.amount);

    if (coinTexture) {
      this.coinSprite = new Sprite(coinTexture);
      this.coinSprite.anchor.set(1, 0.5);
      // Match height to the amount font; preserve aspect ratio.
      const aspect = coinTexture.width / coinTexture.height;
      this.coinSprite.height = COIN_SPRITE_HEIGHT;
      this.coinSprite.width = COIN_SPRITE_HEIGHT * aspect;
      this.addChild(this.coinSprite);
      this.layoutCoin();
    } else {
      this.coinSprite = null;
    }
  }

  setAmount(amount: number): void {
    this.amount.text = amount.toLocaleString();
    if (this.coinSprite) this.layoutCoin();
  }

  /** Position the coin sprite immediately to the left of the amount
   * text, vertically centred on the amount baseline. The sprite uses
   * anchor (1, 0.5) so its right edge sits at the chosen X. */
  private layoutCoin(): void {
    if (!this.coinSprite) return;
    const half = this.amount.width / 2;
    // 6px gap between coin and the leftmost digit.
    this.coinSprite.x = -half - 6;
    this.coinSprite.y = this.amount.y + this.amount.height / 2;
  }

  /** Stage-local position of the amount label (relative to the display's
   * own origin). Useful as a take-win target. */
  get amountPosition(): { x: number; y: number } {
    return { x: this.x, y: this.y + 16 };
  }

  bump(): void {
    this.amount.scale.set(1);
    this.tweens.start({
      durationMs: 180,
      easing: Easings.easeOutCubic,
      onUpdate: (p) => {
        const arc = Math.sin(p * Math.PI);
        this.amount.scale.set(1 + arc * 0.08);
      },
      onComplete: () => {
        this.amount.scale.set(1);
      },
    });
  }

  /** Cancel any in-flight bump animation. Called by host components on
   * destroy so the tween's onComplete doesn't run against destroyed text. */
  destroyDisplay(): void {
    this.tweens.cancelAll();
  }

}
